/**
 * Lingkungan yang sedang dibuka, dibaca dari sasaran API.
 *
 * Dev dan produksi punya dua alamat yang mirip, dan satu-satunya cara membedakannya
 * adalah membacanya. Aturan yang sama dengan bar konteks tenant: **yang menentukan
 * akibat tidak boleh ditebak.**
 *
 * Diangkat dari `Login.jsx` saat fase B, karena chip ini muncul di layar login DAN
 * di rel navigasi kedua shell — dan tiga salinan perhitungan yang sama adalah tiga
 * kesempatan untuk menyimpang.
 */
/**
 * Sasaran API yang dibaca lingkungannya.
 *
 * Di server pengembangan (`npm run dev`) halamannya di localhost sementara datanya milik backend
 * yang diteruskan Vite, jadi sasarannya `VITE_API_TARGET`, bawaannya staging. BUILD YANG DIPASANG
 * memanggil API di alamatnya sendiri (`/api/v1`, diteruskan nginx), jadi sasarannya alamat halaman
 * itu: build yang sama tahu dirinya staging di `staging.smova.id` dan produksi di `smova.id`, tanpa
 * setelan apa pun saat build (ADR-127). Sebelumnya build tanpa setelan selalu mengira dirinya
 * staging, juga di production. `VITE_API_TARGET` yang disebut saat build tetap menang: uji alur
 * memakainya.
 */
export function sasaranDari({ target, pengembangan, asal }) {
  if (target) return target
  if (!pengembangan && asal) return asal
  return 'https://staging.smova.id'
}

const SASARAN = sasaranDari({
  target: import.meta.env.VITE_API_TARGET,
  pengembangan: import.meta.env.DEV,
  asal: typeof window === 'undefined' ? undefined : window.location?.origin,
})

const hostDari = (sasaran) => String(sasaran).replace(/^https?:\/\//, '').replace(/\/$/, '')

// Backend di mesin sendiri (`make contoh`, atau backend lokal) terbaca dev, TETAPI hanya
// saat aplikasinya dijalankan server pengembangan (`npm run dev`). Build yang dipasang
// bisa saja menyebut backend-nya lewat 127.0.0.1 (nginx di VPS meneruskan `/api` ke sana),
// dan build seperti itu tidak boleh membuka `?ui=baru` dan galeri komponen di produksi.
// Dijangkar di kedua ujung, supaya `127.0.0.1.contoh.com` tidak ikut terbaca lokal.
const LOKAL = /^(127\.0\.0\.1|localhost|\[::1\])(:\d+)?$/

// Staging (ADR-123): backend dan data dev di domain sendiri. Disebut dengan namanya, dan
// bukan produksi: `?ui=lama` dan galeri komponen tetap terbuka.
const STAGING = /(^|[.-])staging([.-]|$)/

/**
 * 'dev', 'staging', atau 'produksi' untuk satu sasaran API. `pengembangan`: aplikasinya sedang
 * dijalankan server pengembangan; bawaannya dibaca dari Vite, dan uji menyebutnya sendiri.
 */
export function lingkunganDari(sasaran, { pengembangan = import.meta.env.DEV } = {}) {
  const host = hostDari(sasaran)
  if (STAGING.test(host)) return 'staging'
  return (pengembangan && LOKAL.test(host)) || /(^|[.-])dev/.test(host) ? 'dev' : 'produksi'
}

export const HOST = hostDari(SASARAN)
export const LINGKUNGAN = lingkunganDari(SASARAN)
// "Dev" di sini berarti bukan produksi: staging memakai backend dev.
export const DI_DEV = LINGKUNGAN !== 'produksi'
